import { Link } from "react-router-dom";
import { Heart, ShoppingBag } from "lucide-react";
import { Product } from "@/hooks/useProducts";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { formatPrice } from "@/lib/currency";
import { motion } from "framer-motion";
import { toast } from "sonner";

const ProductCard = ({ product, index = 0, rank }: { product: Product; index?: number; rank?: number }) => {
  const { addToCart } = useCart();
  const { isInWishlist, toggleWishlist } = useWishlist();
  const wishlisted = isInWishlist(product.id);

  const handleQuickAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    addToCart(product, product.sizes?.[1] || "M");
    toast.success(`${product.name} added to cart`);
  };

  const handleWishlist = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    toggleWishlist(product.id);
  };

  const discount = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
    >
      <Link to={`/product/${product.id}`} className="group block">
        <div className="relative overflow-hidden rounded-lg border border-border/50 bg-card transition-all duration-300 hover:border-primary/40 hover-glow">
          {/* Scanline overlay on hover */}
          <div className="absolute inset-0 z-10 scanline-overlay opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />

          {/* Rank badge */}
          {rank && (
            <div className="absolute left-0 top-3 z-20 flex h-7 w-7 items-center justify-center rounded-r-md bg-primary font-display text-sm font-black text-primary-foreground">
              #{rank}
            </div>
          )}

          <div className="relative aspect-square overflow-hidden">
            <img
              src={product.image}
              alt={product.name}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            {product.badge && (
              <span className="absolute left-3 top-3 rounded-sm bg-primary px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary-foreground">
                {product.badge}
              </span>
            )}
            {discount > 0 && (
              <span className="absolute right-3 top-3 rounded-sm bg-destructive px-2 py-0.5 text-[10px] font-bold text-destructive-foreground">
                -{discount}%
              </span>
            )}
            <button
              aria-label={wishlisted ? "Remove from wishlist" : "Add to wishlist"}
              onClick={handleWishlist}
              className={`absolute right-3 bottom-3 z-20 flex h-8 w-8 items-center justify-center rounded-md transition-all ${
                wishlisted
                  ? "bg-destructive text-destructive-foreground"
                  : "bg-background/70 text-muted-foreground backdrop-blur-sm hover:bg-background/90"
              }`}
            >
              <Heart className={`h-4 w-4 ${wishlisted ? "fill-current" : ""}`} />
            </button>
          </div>
          <div className="p-3">
            <p className="text-[10px] font-medium uppercase tracking-wider text-accent">{product.category}</p>
            <h3 className="mt-0.5 font-display text-sm font-semibold leading-tight line-clamp-2">{product.name}</h3>
            <div className="mt-2 flex items-center gap-2">
              <span className="font-display text-lg font-black text-primary glow-text">{formatPrice(product.price)}</span>
              {product.originalPrice && (
                <span className="text-xs text-muted-foreground line-through">{formatPrice(product.originalPrice)}</span>
              )}
            </div>
            <div className="mt-1 flex items-center gap-1">
              <span className="text-xs text-accent">{"★".repeat(Math.floor(product.rating))}</span>
              <span className="text-[10px] text-muted-foreground">({product.reviews})</span>
            </div>
            <button
              onClick={handleQuickAdd}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-md border border-primary/30 bg-primary/10 py-2 text-xs font-bold uppercase tracking-wider text-primary transition-all hover:bg-primary hover:text-primary-foreground glow-neon"
            >
              <ShoppingBag className="h-3.5 w-3.5" /> Add to Cart
            </button>
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default ProductCard;
